import * as React from 'react';
import { cn } from '@/lib/utils';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

/**
 * O campo de texto do clearance. O desenho abaixo nasceu na tela de login e
 * subiu para cá — é o padrão do app, não uma exceção daquela página.
 *
 * ## Tipografia FIXA, não fluida
 *
 * `--text-base` é `clamp(1rem, 0.95rem + 0.2vw, 1.125rem)`: ótimo para texto
 * corrido, mas num desktop de 1280px resolve para 18px e o campo ganha cara de
 * tamanho grande. 16px é o valor certo — e é também o piso que impede o Safari
 * do iPhone de dar zoom ao focar o campo, então não dá para descer mais.
 *
 * ## O placeholder não acompanha o peso do valor
 *
 * O valor é `semibold` para se destacar do rótulo. O placeholder herdaria esse
 * peso e passaria a ter o corpo de um dado digitado — um exemplo de CNPJ lido
 * como campo já preenchido. Volta para 400, e a cor desce junto: o
 * `--muted-foreground` do primitivo anterior dá 8,78:1, contraste de texto de
 * leitura, não de dica. `foreground/60` fica em 4,53:1 — passa no AA, que é o
 * piso para texto, e para de competir com o que a pessoa escreve.
 *
 * ## O foco, no desenho do Stripe
 *
 * Borda de 1px marcando (`primary/60`), halo curto acompanhando
 * (`primary-soft`, 3px). O `--ring` global de 4px é pesado demais aqui e
 * aparece para todo mundo — campo de texto casa com `:focus-visible` também no
 * clique do mouse, não só na navegação por teclado.
 *
 * **A sombra em repouso existe transparente de propósito**, e é isso que a
 * torna animável: `box-shadow` não interpola a partir de `none`. É o mesmo
 * truque que o Stripe usa no campo deles. Sem ela, o foco liga de um quadro
 * para o outro.
 *
 * ## `outline-hidden`, e não um `outline` transparente à mão
 *
 * A primeira versão declarava `outline: 4px solid transparent` em repouso E no
 * foco, para "sobrar como indicador" no alto contraste do Windows. Estava
 * errado, e do jeito pior: a spec (CSS Color Adjustment L1 §3.1) força
 * `outline-color` para cor do sistema e só abre exceção de alfa para
 * `background-color`. Ou seja, naquele modo o contorno aparecia **sempre** — e
 * como repouso e foco declaravam o mesmo valor, o campo focado ficava
 * idêntico ao não focado. Era regressão: antes valia o `*:focus-visible`
 * global, que pinta só no foco.
 *
 * `focus-visible:outline-hidden` é o utilitário que resolve os dois lados: ele
 * desliga o anel global (quem desenha o foco aqui é a sombra) e repõe um
 * outline sob `@media (forced-colors: active)`, só no foco, que é exatamente
 * onde o indicador precisa estar.
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', ...props }, ref) => {
    return (
      <input
        type={type}
        ref={ref}
        className={cn(
          'flex h-11 w-full rounded-md border border-border-field bg-background px-3.5 py-2',
          'text-[1rem] font-semibold text-foreground',
          'placeholder:font-normal placeholder:text-foreground/60',
          'shadow-[0_0_0_3px_transparent]',
          'focus-visible:border-primary/60 focus-visible:outline-hidden',
          'focus-visible:shadow-[0_0_0_3px_var(--primary-soft)]',
          'transition-[color,background-color,border-color,box-shadow]',
          'duration-300 ease-out',
          // 45% (2,88:1). Os 30% de antes davam 1,98:1 — mais FRACO que o
          // repouso mauve (2,16:1), então passar o mouse clareava o contorno em
          // vez de reforçá-lo.
          'hover:border-foreground/45',
          'file:border-0 file:bg-transparent file:text-sm file:font-medium',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input };
